import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Dropdown from "../common/Dropdown";
import {
  screenSchema,
  createScreenSchema,
  ScreenFormData,
  Screen,
} from "../../schemas/screenSchema";
import Button from "../common/Button";
import LocationPicker from "./LocationPicker";
import { useCityOptions } from "../../hooks/useCityOptions";
import { useScreenTagOptions } from "../../hooks/useScreenTagOptions";
import { useRegionOptions } from "../../hooks/useRegionOptions";
import { useRestaurantOptions } from "../../hooks/useRestaurantOptions";
import { mergeFormDefaults, cleanFormData } from "../../utils/formUtils";

interface ScreenFormProps {
  initialData?: Partial<ScreenFormData> & { region?: Screen["region"] };
  onSubmit: (data: ScreenFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isOpen?: boolean;
}

const ScreenForm: React.FC<ScreenFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isOpen = true,
}) => {
  const isCreating = !initialData;
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ScreenFormData>({
    resolver: zodResolver(
      isCreating ? createScreenSchema : screenSchema,
    ) as any,
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: mergeFormDefaults(
      {
        screenName: "",
        address: "",
        orientation: "",
        verificationCode: "",
      },
      initialData,
    ) as ScreenFormData,
  });

  const { cityOptions, isCityOptionsLoading } = useCityOptions();
  const [selectedCityId, setSelectedCityId] = useState<string | undefined>();
  const { regionOptions, isRegionOptionsLoading } =
    useRegionOptions(selectedCityId);
  const { screenTagOptions, isScreenTagOptionsLoading } = useScreenTagOptions();
  const { restaurantOptions, isRestaurantOptionsLoading } =
    useRestaurantOptions();

  const orientationOptions = [
    { value: "portrait", label: "Portrait" },
    { value: "landscape", label: "Landscape" },
  ];

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);
  // Track initial orientation to detect changes
  const initialOrientationRef = useRef<string | undefined>(undefined);
  // Address the map pin was last resolved for. Leaving the address field
  // unchanged must not re-geocode, or it would overwrite a pin the user
  // dragged into place.
  const lastLocatedAddressRef = useRef("");
  const [locateAddress, setLocateAddress] = useState("");

  // Set initial city from region when form loads with existing data
  useEffect(() => {
    if (initialData?.region?.city?.id && !selectedCityId) {
      setSelectedCityId(initialData.region.city.id);
    }
  }, [initialData, selectedCityId]);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset(
          mergeFormDefaults(
            {
              screenName: "",
              address: "",
              orientation: "",
              verificationCode: "",
            },
            initialData,
          ) as ScreenFormData,
        );
        // Set initial city from region when form loads with existing data
        if (initialData?.region?.city?.id) {
          setSelectedCityId(initialData.region.city.id);
        }
        // Store initial orientation for change detection
        initialOrientationRef.current = initialData?.orientation || undefined;
        // An existing pin counts as already located for its saved address.
        lastLocatedAddressRef.current =
          typeof initialData.latitude === "number" &&
          typeof initialData.longitude === "number"
            ? (initialData.address || "").trim()
            : "";
        setLocateAddress("");
      } else {
        reset({
          screenName: "",
          address: "",
          orientation: "",
          verificationCode: "",
        } as ScreenFormData);
        // Clear initial orientation for create mode
        initialOrientationRef.current = undefined;
        lastLocatedAddressRef.current = "";
        setLocateAddress("");
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  // Set orientation value when initialData changes
  useEffect(() => {
    if (initialData?.orientation) {
      setValue("orientation", initialData.orientation, {
        shouldValidate: true,
      });
    }
  }, [initialData, setValue]);

  // When city changes (user selection), reset regionId if it doesn't belong to the new city
  useEffect(() => {
    if (selectedCityId && watch("regionId") && regionOptions?.length > 0) {
      const currentRegion = regionOptions.find(
        (r: any) => r.value === watch("regionId"),
      );
      if (!currentRegion) {
        // Only reset if we're not in the initial load phase
        // Check if the regionId from initialData matches any region in the new city
        if (
          initialData?.regionId &&
          watch("regionId") === initialData.regionId
        ) {
          // This is initial load, don't reset yet - wait for regions to load
          return;
        }
        setValue("regionId", undefined);
      }
    }
  }, [selectedCityId, setValue, watch, regionOptions, initialData]);

  const handleAddressBlur = () => {
    trigger("address");
    const address = (getValues("address") || "").trim();
    if (address && address !== lastLocatedAddressRef.current) {
      lastLocatedAddressRef.current = address;
      setLocateAddress(address);
    }
  };

  const onFormSubmit = (data: any) => {
    // Get current values to ensure we have the latest form state
    // This is important in production builds where refs might not be read correctly
    const currentValues = getValues();
    const formData = { ...currentValues, ...data };

    // Clean up: convert null values to undefined before submission
    const cleanedData = cleanFormData(formData);

    // Remove 'region' object if present - backend expects 'regionId' only
    // Use delete to ensure it's completely removed even if destructuring doesn't work
    const submissionData = { ...cleanedData };
    delete (submissionData as any).region;

    onSubmit(submissionData as ScreenFormData);
  };

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="space-y-4"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Screen Name"
          placeholder="Screen Name"
          name="screenName"
          value={watch("screenName") || ""}
          onChange={(e) =>
            setValue("screenName", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("screenName")}
          error={(errors as any).screenName}
        />
        <Input
          label={isCreating ? "Verification Code *" : "Verification Code"}
          placeholder="Enter verification code"
          name="verificationCode"
          value={watch("verificationCode") || ""}
          onChange={(e) =>
            setValue("verificationCode", e.target.value, {
              shouldValidate: false,
            })
          }
          onBlur={() => trigger("verificationCode")}
          error={(errors as any).verificationCode}
        />
        <Dropdown
          label="Tags"
          multiple
          searchable
          options={(screenTagOptions as any) || []}
          value={watch("tagIds")?.map((id: string) => String(id)) || []}
          onChange={(vals) =>
            setValue("tagIds", (vals as string[]) || [], {
              shouldValidate: false,
            })
          }
          onBlur={() => trigger("tagIds")}
          error={(errors as any).tagIds}
          disabled={isScreenTagOptionsLoading}
          name="tagIds"
        />
        <Dropdown
          label="Orientation"
          options={orientationOptions}
          value={watch("orientation") || ""}
          onChange={(val) => {
            const newOrientation = val as string;
            const currentOrientation = watch("orientation") || "";

            // Only show confirmation if:
            // 1. We're in update mode (initialData exists)
            // 2. There's an initial orientation value
            // 3. The new orientation is different from the initial one
            // 4. The new orientation is different from the current one
            if (
              !isCreating &&
              initialOrientationRef.current &&
              newOrientation !== initialOrientationRef.current &&
              newOrientation !== currentOrientation
            ) {
              const confirmed = window.confirm(
                "Do you really want to change the orientation of the screen?",
              );

              if (confirmed) {
                setValue("orientation", newOrientation, {
                  shouldValidate: false,
                });
              }
              // If not confirmed, the value remains unchanged (no need to setValue)
            } else {
              // For create mode or when orientation hasn't changed from initial, allow change
              setValue("orientation", newOrientation, {
                shouldValidate: false,
              });
            }
          }}
          onBlur={() => trigger("orientation")}
          error={(errors as any).orientation}
          name="orientation"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Dropdown
            label="Restaurant"
            searchable
            options={(restaurantOptions as any) || []}
            value={watch("restaurantId") ? String(watch("restaurantId")) : ""}
            onChange={(val) =>
              setValue("restaurantId", val as string, { shouldValidate: false })
            }
            onBlur={() => trigger("restaurantId")}
            error={(errors as any).restaurantId}
            disabled={isRestaurantOptionsLoading}
            name="restaurantId"
          />
          <Dropdown
            label="City"
            searchable
            options={(cityOptions as any) || []}
            value={selectedCityId || ""}
            onChange={(val) => {
              setSelectedCityId(val as string);
              setValue("regionId", undefined, { shouldValidate: false });
            }}
            disabled={isCityOptionsLoading}
          />
          <Dropdown
            label="Region"
            searchable
            options={(regionOptions as any) || []}
            value={watch("regionId") ? String(watch("regionId")) : ""}
            onChange={(val) =>
              setValue("regionId", val as string, { shouldValidate: false })
            }
            onBlur={() => trigger("regionId")}
            error={(errors as any).regionId}
            disabled={!selectedCityId || isRegionOptionsLoading}
            name="regionId"
          />
          <Input
            label="Address"
            placeholder="Address"
            name="address"
            value={watch("address") || ""}
            onChange={(e) =>
              setValue("address", e.target.value, { shouldValidate: false })
            }
            onBlur={handleAddressBlur}
            error={(errors as any).address}
          />
        </div>
        <LocationPicker
          address={watch("address") || ""}
          latitude={watch("latitude")}
          longitude={watch("longitude")}
          locateAddress={locateAddress}
          onChange={(lat, lng) => {
            setValue("latitude", lat, { shouldValidate: false });
            setValue("longitude", lng, { shouldValidate: false });
          }}
        />
      </div>
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {initialData ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default ScreenForm;
